import { useRef, useState } from "react";
import { CARD_CATEGORY_COLOR, type CardView } from "./cardViews";
import { playCue } from "../../audio/AudioEngine";

interface CardProps {
  card: CardView;
  /** This specific hand copy — threaded through to the callbacks, not used for rendering. */
  instanceId: string;
  /** Fan layout: horizontal offset, arc lift, and rotation from Hand. */
  offsetX: number;
  lift: number;
  rotation: number;
  affordable: boolean;
  /** This card's hand slot is one Chemical Hazard Spill has locked. */
  locked: boolean;
  /** If this card is bound to a hardware node, the human-readable description of that binding. */
  boundNodeLabel: string | null;
  onDragStart: (card: CardView, instanceId: string, x: number, y: number) => void;
  onActivate: (card: CardView, instanceId: string) => void;
}

/**
 * An illustrated plastic schematic tile, per `04-art-direction.md` — not a flat UI
 * rectangle. Hover raises it out of the fan and tilts it in 3D toward the
 * cursor; pressing it picks it up.
 */
export function Card({
  card,
  instanceId,
  offsetX,
  lift,
  rotation,
  affordable,
  locked,
  boundNodeLabel,
  onDragStart,
  onActivate,
}: CardProps) {
  const [hovered, setHovered] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const ref = useRef<HTMLDivElement>(null);

  const handlePointerMove = (e: React.PointerEvent) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Normalized -0.5..0.5 across the card, used as a tilt axis.
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: -py * 18, y: px * 18 });
  };

  const accent = CARD_CATEGORY_COLOR[card.category];
  // Locked and unplayable are their own dead-draw states, distinct from
  // "unaffordable" — a player can't fix either by freeing up kW, so they get
  // their own geometry and words rather than sharing the greyscale filter
  // (ui-and-accessibility.md: hazard state is readable without colour too).
  const playable = affordable && !locked && card.isPlayable;
  const stateNote = [
    card.isPlayable ? null : "cannot be played",
    locked ? "locked in hand" : null,
    boundNodeLabel ? `bound to ${boundNodeLabel}` : null,
  ]
    .filter((n): n is string => n !== null)
    .join(", ");

  return (
    <div
      ref={ref}
      className={`card${hovered ? " is-hovered" : ""}${affordable ? "" : " is-unaffordable"}${
        locked ? " is-locked" : ""
      }${card.isPlayable ? "" : " is-unplayable"}`}
      role="button"
      aria-disabled={!playable}
      // The card's own text is spread across six elements and reads as a
      // jumble; one label says the whole thing in the order it matters. The
      // state note carries the same "locked" / "cannot be played" information
      // the badges give visually, so the keyboard path never falls behind.
      aria-label={`${card.name}, ${card.category}, ${card.costKw} kilowatts.${
        stateNote ? ` ${stateNote}.` : ""
      } ${card.blurb}`}
      style={{
        transform: [
          `translateX(${offsetX}px)`,
          `translateY(${hovered ? lift - 34 : lift}px)`,
          `rotate(${hovered ? rotation * 0.35 : rotation}deg)`,
          `perspective(700px)`,
          `rotateX(${hovered ? tilt.x : 0}deg)`,
          `rotateY(${hovered ? tilt.y : 0}deg)`,
          `scale(${hovered ? 1.06 : 1})`,
        ].join(" "),
        zIndex: hovered ? 20 : 1,
      }}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => {
        setHovered(false);
        setTilt({ x: 0, y: 0 });
      }}
      onPointerMove={handlePointerMove}
      onPointerDown={(e) => {
        if (!playable) return;
        if (card.target === "instant") {
          onActivate(card, instanceId);
          return;
        }
        playCue("cardPick");
        onDragStart(card, instanceId, e.clientX, e.clientY);
      }}
    >
      <div className="card__accent" style={{ background: accent }} />
      <div className="card__cost">{card.costKw}<span>kW</span></div>
      {(locked || !card.isPlayable || boundNodeLabel) && (
        <div className="card__badges">
          {!card.isPlayable && (
            <span className="card__badge card__badge--unplayable">
              <BlockedGlyph />
              cannot play
            </span>
          )}
          {locked && (
            <span className="card__badge card__badge--locked">
              <LockGlyph />
              locked
            </span>
          )}
          {boundNodeLabel && (
            <span className="card__badge card__badge--bound" title={`bound to ${boundNodeLabel}`}>
              <BoundGlyph />
              bound
            </span>
          )}
        </div>
      )}
      <div className="card__name">{card.name}</div>
      <div className="card__art" style={{ borderColor: accent }}>
        <CardGlyph category={card.category} accent={accent} />
      </div>
      <div className="card__blurb">{card.blurb}</div>
      <div className="card__category">{card.category}</div>
    </div>
  );
}

/** Schematic-style glyph standing in for the illustrated hardware plate. */
function CardGlyph({ category, accent }: { category: CardView["category"]; accent: string }) {
  switch (category) {
    case "hardware":
      return (
        <svg viewBox="0 0 60 40" aria-hidden>
          <rect x="8" y="14" width="20" height="16" rx="3" fill={accent} stroke="#2B2118" strokeWidth="2" />
          <rect x="34" y="10" width="18" height="20" rx="3" fill="none" stroke="#2B2118" strokeWidth="2" />
          <path d="M28 22h6" stroke="#2B2118" strokeWidth="2.5" />
        </svg>
      );
    case "metaUtility":
      return (
        <svg viewBox="0 0 60 40" aria-hidden>
          <rect x="14" y="10" width="32" height="22" rx="2" fill="none" stroke="#2B2118" strokeWidth="2" />
          <path d="M18 17h24M18 23h24M18 29h16" stroke={accent} strokeWidth="2.5" />
        </svg>
      );
    case "attachment":
      return (
        <svg viewBox="0 0 60 40" aria-hidden>
          <rect x="16" y="12" width="28" height="18" rx="4" fill="none" stroke="#2B2118" strokeWidth="2" />
          <path d="M16 21h28M24 12v18M36 12v18" stroke={accent} strokeWidth="2.5" />
        </svg>
      );
    case "directive":
      return (
        <svg viewBox="0 0 60 40" aria-hidden>
          <path d="M30 6l5 12 12 2-9 8 3 12-11-6-11 6 3-12-9-8 12-2z" fill={accent} stroke="#2B2118" strokeWidth="2" strokeLinejoin="round" />
        </svg>
      );
    case "hazard":
      return (
        <svg viewBox="0 0 60 40" aria-hidden>
          <path d="M30 8l16 24H14z" fill={accent} stroke="#2B2118" strokeWidth="2" strokeLinejoin="round" />
          <path d="M30 18v7" stroke="#2B2118" strokeWidth="2.5" />
        </svg>
      );
  }
}

/**
 * Padlock outline for a hand slot Chemical Hazard Spill has locked shut —
 * geometry carries the state, same rule as `CardGlyph` (ui-and-accessibility.md).
 */
function LockGlyph() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden>
      <rect x="5" y="9" width="10" height="8" rx="1.5" fill="none" stroke="#2B2118" strokeWidth="1.6" />
      <path d="M7 9V6a3 3 0 0 1 6 0v3" fill="none" stroke="#2B2118" strokeWidth="1.6" />
    </svg>
  );
}

/** X-through-circle for a card that cannot be played at all. */
function BlockedGlyph() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden>
      <circle cx="10" cy="10" r="7" fill="none" stroke="#2B2118" strokeWidth="1.6" />
      <path d="M5.5 5.5l9 9" stroke="#2B2118" strokeWidth="1.6" />
    </svg>
  );
}

/** Chain link for a card that is bound to a hardware node. */
function BoundGlyph() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden>
      <path
        d="M6 8a2 2 0 0 0 0 4h8a2 2 0 0 0 0-4m-8 0a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2m-8 4a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2"
        fill="none"
        stroke="#2B2118"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
